import { Button, Input } from "@drs-ops/ui";
import { ConfirmDialog, LoadingSpinner } from "@drs-ops/ui/feedback";
import { useDebounce } from "@drs-ops/ui/hooks";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { AirportRow } from "@/components/settings/AirportRow";
import { useAdminApi } from "@/hooks/useAdminApi";
import type { Airport } from "@/types/airport";

type SortCol = "iata" | "name" | "city" | "state" | "hub_size" | "delta_served";

export function AirportsTab(): React.ReactElement {
  const api = useAdminApi();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const debouncedQ = useDebounce(q, 300);
  const [deltaOnly, setDeltaOnly] = useState(false);
  const [reseedConfirm, setReseedConfirm] = useState(false);
  const [sort, setSort] = useState<{ col: SortCol; dir: "asc" | "desc" }>({ col: "iata", dir: "asc" });

  const list = useQuery({
    queryKey: ["airports", debouncedQ, deltaOnly, sort.col, sort.dir],
    queryFn: () => {
      const params = new URLSearchParams();
      if (debouncedQ.length > 0) params.set("q", debouncedQ);
      if (deltaOnly) params.set("delta_only", "true");
      params.set("sort", sort.col);
      params.set("direction", sort.dir);
      return api.get<Airport[]>(`/api/airports?${params.toString()}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (args: { iata: string; body: Partial<Airport> }) =>
      api.patch<Airport>(`/api/airports/${args.iata}`, args.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["airports"] }),
  });

  const reseedMutation = useMutation({
    mutationFn: () => api.post<{ inserted: number }>("/api/airports/reseed"),
    onSuccess: () => {
      setReseedConfirm(false);
      qc.invalidateQueries({ queryKey: ["airports"] });
    },
  });

  function handleSort(col: SortCol) {
    setSort((prev) => (prev.col === col ? { col, dir: prev.dir === "asc" ? "desc" : "asc" } : { col, dir: "asc" }));
  }

  function SortIndicator({ col }: { col: SortCol }) {
    if (sort.col !== col) return null;
    return <span className="ml-1">{sort.dir === "asc" ? "▲" : "▼"}</span>;
  }

  const cols: { key: SortCol; label: string }[] = [
    { key: "iata", label: "IATA" },
    { key: "name", label: "Name" },
    { key: "city", label: "City" },
    { key: "state", label: "State" },
    { key: "hub_size", label: "Hub size" },
    { key: "delta_served", label: "Delta" },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Input
          placeholder="Search by IATA, name, or city"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-sm"
        />
        <label className="flex items-center gap-1 text-sm">
          <input type="checkbox" checked={deltaOnly} onChange={(e) => setDeltaOnly(e.target.checked)} /> Delta-served
          only
        </label>
        <div className="flex-1" />
        <Button variant="outline" onClick={() => setReseedConfirm(true)}>
          Reseed airports
        </Button>
      </div>

      {list.isLoading ? (
        <LoadingSpinner className="my-12" />
      ) : (
        <div className="rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50 text-left">
              <tr>
                {cols.map(({ key, label }) => (
                  <th key={key} className="p-2">
                    <button
                      type="button"
                      className="flex items-center font-semibold hover:text-foreground"
                      onClick={() => handleSort(key)}
                    >
                      {label}
                      <SortIndicator col={key} />
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(list.data ?? []).map((a) => (
                <AirportRow
                  key={a.iata}
                  airport={a}
                  onDeltaChange={(iata, delta) =>
                    updateMutation.mutate({ iata, body: { delta_served: delta ? 1 : 0 } })
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={reseedConfirm}
        title="Reseed airports from OurAirports?"
        message="This pulls the latest US commercial airport data and preserves your existing Delta-served selections. Takes a few seconds."
        onConfirm={() => reseedMutation.mutate()}
        onCancel={() => setReseedConfirm(false)}
      />
    </div>
  );
}
